<template>
    <q-page class="public-view form" :class="{ mfa_form: mfaRequired }">
        <q-card-section>
            <q-banner
                id="login-error-banner"
                v-if="errorMessage"
                class="background-red border-red full-width q-mb-md error-banner"
                :class="$q.platform.is.ipad ? 'q-mb-sm' : ''"
                rounded
                inline-actions
            >
                <template v-slot:avatar>
                    <q-icon
                        name="fa-light fa-circle-exclamation"
                        color="negative"
                        class="q-pt-sm"
                        size="sm"
                    />
                </template>
                <span>{{ errorMessage }}</span>
            </q-banner>
            <!-- login form -->
            <q-form
                v-if="!showOtp && !mfaRequired"
                id="loginForm"
                method="post"
                @submit.prevent="submit"
                class="q-pt-md q-px-md"
            >
                <div v-if="form.step === this.steps.TENANT_SELECTION">
                    <q-input
                        v-if="isMobile"
                        class="q-mb-md"
                        outlined
                        lazy-rules
                        v-model="form.account"
                        :label="$t('Account')"
                        color="accent"
                        :rules="[
                            val =>
                                val !== '' ||
                                $t('Enter the your company account')
                        ]"
                    >
                        <template v-slot:append>
                            <div style="font-size: 13px">.iotorq-lean.com</div>
                        </template>
                    </q-input>
                    <q-btn
                        :label="$t('Next')"
                        id="login-button"
                        color="primary"
                        class="full-width q-mb-md"
                        type="submit"
                        :loading="form.accountLoading"
                        :disable="form.accountLoading"
                    />
                </div>
                <div v-if="form.step === this.steps.LOGIN_FORM">
                    <div v-if="isMobile" class="row q-mb-lg items-center">
                        <div class="col-9">
                            <div
                                class="text-caption text-secondary text-uppercase"
                            >
                                {{ $t('Account') }}
                            </div>
                            <div class="text-bold text-capitalize">
                                {{ form.account }}
                            </div>
                        </div>
                        <div class="col-3">
                            <q-btn
                                :label="$t('Switch')"
                                dense
                                id="login-button"
                                color="primary"
                                outline
                                class="full-width"
                                @click="form.step = this.steps.TENANT_SELECTION"
                            />
                        </div>
                    </div>
                    <q-input
                        class="q-mb-md"
                        type="text"
                        name="username"
                        autocomplete="username"
                        outlined
                        lazy-rules
                        for="username-field"
                        v-model="form.username"
                        :label="$t('Username')"
                        color="accent"
                        :rules="[
                            val => val !== '' || $t('Enter your username')
                        ]"
                    />
                    <q-input
                        class="q-mb-md"
                        outlined
                        lazy-rules
                        for="password-field"
                        v-model="form.password"
                        autocomplete="current-password new-password"
                        :type="showPassword ? 'input' : 'password'"
                        color="accent"
                        name="password"
                        :label="$t('Password')"
                        :rules="[
                            val => val !== '' || $t('Enter your password')
                        ]"
                    >
                        <template v-slot:append>
                            <q-icon
                                v-show="form.password.length > 0"
                                @click="showPassword = !showPassword"
                                :name="
                                    showPassword
                                        ? 'fas fa-eye-slash'
                                        : 'fas fa-eye'
                                "
                                class="cursor-pointer"
                            />
                        </template>
                    </q-input>

                    <q-btn
                        :label="$t('Login')"
                        id="login-button"
                        type="submit"
                        color="primary"
                        class="full-width"
                    />
                    <q-btn
                        class="q-mt-md full-width"
                        @click="
                            $router.push({
                                name: 'reset_password'
                            })
                        "
                        :label="$t('Reset my password')"
                        text-color="primary"
                        color="white"
                        id="reset-button"
                        outline
                    />

                    <div
                        class="q-mt-md text-center or-separator text-secondary"
                        v-if="ssoProviders.length > 0"
                    >
                        <q-separator class="separator" />
                        <span class="or-text q-mx-md">{{ $t('Or') }}</span>
                        <q-separator class="separator" />
                    </div>

                    <SSOButton
                        v-for="provider in ssoProviders"
                        :key="provider.type"
                        :authURL="provider.auth_url"
                        :providerType="provider.type"
                    />
                </div>
                <div class="text-caption text-center text-grey-9" align="right">
                    <div class="q-mt-lg">
                        © 2021-{{ getCurrentYear }} ioTORQ LEAN ·
                        <a
                            class="text-accent"
                            :href="privacyPolicyUrl"
                            target="_black"
                        >
                            Privacy Policy
                        </a>
                        ·
                        <a
                            class="text-accent"
                            :href="termsAndConditionsUrl"
                            target="_black"
                        >
                            Terms & Conditions
                        </a>
                    </div>
                    <div class="q-my-sm">
                        {{ $t('Version') + ' ' + version }}
                    </div>
                </div>
            </q-form>
            <div v-else-if="mfaRequired" class="row items-center">
                <div class="mfa-panel-header">
                    <div class="text-h6 full-width">
                        {{ $t('Setup Multi-factor authentication') }}
                    </div>

                    <span class="text-secondary q-mx-md full-width">
                        {{
                            $t(
                                'You must enable one of the following MFA methods in order to login:'
                            )
                        }}
                    </span>
                </div>

                <MFAPanel
                    @mfaActivated="redirectUserToDashboard"
                    :activeMethod="activeMFAMethod"
                />

                <!-- Go back to login button in a div-->
                <div class="row full-width q-mt-sm">
                    <q-btn
                        :label="$t('Return to login')"
                        outline
                        class="full-width"
                        color="secondary"
                        @click="mfaRequired = false"
                    />
                </div>
            </div>
            <div v-else class="q-px-sm">
                <div class="text-h6 q-my-md text-center">
                    {{ otpInputHeaderText }}
                </div>
                <div class="text-center">
                    {{ otpInputDescriptionText }}
                </div>
                <div class="row justify-center">
                    <one-time-password-input
                        class="q-my-lg"
                        :length="otpLength"
                        @update:modelValue="otp => (oneTimePassword = otp)"
                        @passwordIsValid="val => handleOTP(val)"
                        :key="`otp-${submitCounter}`"
                    />
                </div>
                <div
                    v-if="activeMFAMethod === 'email'"
                    class="row justify-center full-width q-mb-sm"
                >
                    <p class="text-secondary">
                        {{ $t("Didn't receive code?") }}
                        <!-- Clickable text -->
                        <span
                            class="text-primary cursor-pointer"
                            @click="sendOTPToEmail()"
                            :class="{
                                disabled: emailOTPCoolDownSeconds > 0
                            }"
                            id="resend-email-otp"
                        >
                            {{ $t('Resend code') }}
                            <span v-if="emailOTPCoolDownSeconds > 0">
                                ({{ emailOTPCoolDownSeconds }})
                            </span>
                        </span>
                    </p>
                </div>

                <q-btn
                    :label="$t('Submit')"
                    type="submit"
                    color="primary"
                    class="q-mb-md full-width"
                    :disable="!isOtpValid"
                    @click="validateOTP"
                />
                <q-btn
                    :label="$t('Back')"
                    outline
                    color="secondary"
                    class="q-mb-md full-width"
                    @click="
                        () => {
                            ephemeralToken = '';
                            errorMessage = null;
                        }
                    "
                />
                <div class="text-grey q-mb-md text-center">
                    {{
                        $t(
                            "If you don't have access to this verification method, you can still login using any of your backup codes."
                        )
                    }}
                </div>
            </div>
        </q-card-section>
    </q-page>
</template>

<script>
import appVersion from '@baseDir/appVersion.json';
import OneTimePasswordInput from '@components/OneTimePasswordInput.vue';
import MFAPanel from '@components/MFAPanel.vue';
import { methodTypeToLabel } from './utils.js';
import { mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useSharedStore } from '@stores/shared.js';
import SSOButton from '@components/SSOButton.vue';

export default {
    name: 'Login',
    components: {
        OneTimePasswordInput,
        MFAPanel,
        SSOButton
    },
    async created() {
        await this.initializeForm();

        const tenant = this.$storage.getItem('tenant');
        if (tenant) {
            this.form.account = tenant;
        }
        const userName = this.$storage.getItem('userName');
        if (userName) {
            this.form.username = userName;
        }
    },
    beforeUnmount() {
        this.errorMessage = null;
    },
    data() {
        return {
            methodTypeToLabel,
            steps: {
                TENANT_SELECTION: 'tenantSelection',
                LOGIN_FORM: 'login'
            },
            form: {
                account: '',
                username: '',
                password: '',
                step: null,
                accountLoading: false
            },
            // by default assume its a valid tenant or user is using web app.
            isValidTenant: true,
            showPassword: false,
            errorMessage: null,
            // app versioning
            version: appVersion.version,
            // MFA
            otpLength: 6,
            oneTimePassword: '',
            ephemeralToken: '',
            isOtpValid: false,
            mfaRequired: false,
            mfaSecret: null,
            showBackupCodesDialog: false,
            backupCodes: [],
            privacyPolicyUrl: 'https://panevo.com/privacy-policy/',
            termsAndConditionsUrl: 'https://panevo.com/terms-and-conditions/',

            // MFA
            activeMFAMethod: null,
            emailOTPCoolDownSeconds: 0,
            emailOTPResendInterval: null,
            EMAIL_OTP_MAX_COOLDOWN_S: 60,
            submitCounter: 0, // Used to reset OTP input each time it is used

            // SSO
            ssoProviders: []
        };
    },
    computed: {
        /**
         * Show OTP input if user has MFA enabled.
         * @return {Boolean} - True if ephemeral token exists.
         */
        showOtp() {
            return this.ephemeralToken !== '';
        },
        /**
         * Get current year.
         * @return {Number} - Current year.
         */
        getCurrentYear() {
            return new Date().getFullYear();
        },
        /**
         * Is mobile.
         * @return {Boolean} - True if mobile.
         */
        isMobile() {
            return this.$q.platform.is.nativeMobile;
        },
        /**
         * OTP Input header text.
         * @return {String} - OTP Input header text.
         */
        otpInputHeaderText() {
            if (this.activeMFAMethod === 'email') {
                return this.$t('Enter the code we sent to your email');
            } else if (this.activeMFAMethod === 'app') {
                return this.$t(
                    'Enter the code generated by your authenticator app'
                );
            }
            return this.$t('Verification');
        },
        /**
         * OTP Input description text.
         * @return {String} - OTP Input description text.
         */
        otpInputDescriptionText() {
            if (this.activeMFAMethod === 'email') {
                return this.$t(
                    'It may take a few minutes to arrive — be sure to check your spam folder too.'
                );
            } else if (this.activeMFAMethod === 'app') {
                // authenticator app
                return this.$t('Open your authenticator app to view the code.');
            }
            return '';
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useAuthStore, [
            'loadCurrentUserAction',
            'authenticateAction',
            'setJWTAction',
            'verifyAndSetTenantAction',
            'validateOtpAction',
            'loadSubscribedCausesAction'
        ]),
        /**
         * Initialize form.
         */
        async initializeForm() {
            // Web applications do not need to select tenant
            if (!this.isMobile) {
                this.form.step = this.steps.LOGIN_FORM;
                await this.loadSSOProviders();
            } else {
                this.form.step = this.steps.TENANT_SELECTION;
            }
        },
        /**
         * Redirect user to dashboard.
         */
        async redirectUserToDashboard() {
            await this.loadUser();
            const { redirect, ...query } = this.$route.query;
            if (redirect) {
                // include the query parameters and has in the redirect
                console.debug('Redirecting to: ', redirect);
                this.$router.push({
                    path: redirect,
                    query,
                    hash: this.$route.hash
                });
            } else {
                // Redirect to the facility list page
                this.$router.push({
                    name: 'facility_list'
                });
            }
        },
        /**
         * Authenticate user.
         * @return {Promise} - Promise object.
         */
        async authenticateUser() {
            return await this.authenticateAction({
                username: this.form.username,
                password: this.form.password
            });
        },
        /**
         * Get the user's details and load it in the store.
         */
        async loadUser() {
            try {
                await this.loadCurrentUserAction();
                await this.loadSubscribedCausesAction();
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to load user profile. Please try again later.'
                    ),
                    error
                );
            }
        },
        /**
         * Store the user's authentication tokens in the store.
         * @param {String} access
         * @param {String} refresh
         */
        storeAuthTokens(access, refresh) {
            // set tokens in store
            const tokens = {
                access: access,
                refresh: refresh
            };
            this.setJWTAction(tokens);
        },
        /**
         * Handle user login.
         */
        async loginUser() {
            try {
                // authenticate the user
                let response = await this.authenticateUser();
                this.errorMessage = null;
                if ('ephemeral_token' in response.data) {
                    this.ephemeralToken = response.data['ephemeral_token'];
                } else {
                    // store the user tokens
                    this.storeAuthTokens(
                        response.data.access,
                        response.data.refresh
                    );
                    if (response.data['mfa_required']) {
                        this.mfaRequired = true;
                    } else {
                        await this.redirectUserToDashboard();
                    }
                }

                if ('method' in response.data) {
                    this.activeMFAMethod = response.data.method;

                    if (response.data.method === 'email') {
                        this.startResendOTPCoolDown();
                    }
                }
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                let errorMessage = this.$t('Error while trying to login');

                if (typeof error === 'object' && 'error' in error) {
                    if (error.error.match(/credentials/)) {
                        // display a account lockout message informing
                        // user that account will be locked out after 5
                        // attempts
                        errorMessage = this.$t(
                            'Please enter a correct username and password. After 10 failed attempts, logins will be restricted for 30 minutes.'
                        );
                    } else if (error.error.match(/disabled/)) {
                        errorMessage = this.$t(
                            "This account is disabled. Please check your email inbox to activate your account or contact support at support{'@'}iotorq-lean.com."
                        );
                    } else if (error.code === 'password_reset_required') {
                        const { uid, token } = this.extractUidAndToken(
                            error?.reset_link
                        );
                        if (uid && token) {
                            // Redirect user to password reset page.
                            this.$router.push({
                                name: 'reset_password_confirm',
                                params: { uid, token },
                                query: { showResetPasswordMessage: true }
                            });
                        } else {
                            // display message if uid and token are not present
                            errorMessage = this.$t(
                                'Your account requires a password reset before you can log in. Please reset your password to continue.'
                            );
                        }
                    }
                } else {
                    if (
                        typeof error === 'object' &&
                        'account_lockout' in error
                    ) {
                        // display a account lockout message informing
                        // user that account is locked out and they will
                        // be able to login back again in 30 minutes or they
                        // can contact support for immediate access.
                        errorMessage = this.$t(
                            "Too many login attempts with this username. Please try again in 30 minutes or contact support{'@'}iotorq-lean.com for immediate assistance."
                        );
                    }
                }
                this.displayLoginFormErrorMessage(errorMessage);
            }
        },
        /**
         * Handle tenant verification.
         */
        async verifyTenant() {
            if (!this.isMobile) {
                return;
            }

            // if we are on a mobile app, we need to check if the current tenant is valid
            try {
                this.form.accountLoading = true;
                this.isValidTenant = await this.verifyAndSetTenantAction(
                    this.form.account
                );

                // if 404 error is returned or if public tenant is returned
                if (!this.isValidTenant) {
                    this.displayLoginFormErrorMessage(
                        this.$t('No matching company was found.')
                    );
                } else {
                    await this.loadSSOProviders();
                    this.form.step = this.steps.LOGIN_FORM;
                }
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                this.isValidTenant = false;

                // if network request times out and crosses the default threshold
                if (error?.code === 'ECONNABORTED') {
                    this.$notifyError(
                        this.$t('Connection timed out. Please try again.'),
                        error
                    );
                }
                // If user is offline, then it is a network issue on users end
                else if (error?.message === 'Network Error') {
                    this.$notifyError(
                        this.$t(
                            'Issue with network connectivity. Please check your internet connection.'
                        ),
                        error
                    );
                } else if (error?.detail === 'Not found.') {
                    this.displayLoginFormErrorMessage(
                        this.$t('No matching company was found.')
                    );
                } else {
                    this.$notifyError(
                        this.$t('Error while trying to verify company.'),
                        error
                    );
                }
            } finally {
                this.form.accountLoading = false;
            }
        },
        /**
         * Store login details in local storage.
         * This is used to pre-fill the login form.
         */
        storeLoginDetails() {
            this.$storage.setItem('userName', this.form.username);
        },
        /**
         * Handle form submission
         */
        async submit() {
            this.errorMessage = '';

            if (this.form.step === this.steps.TENANT_SELECTION) {
                await this.verifyTenant();
                return;
            } else if (this.form.step === this.steps.LOGIN_FORM) {
                this.beginLoading(`${this.$t('Authenticating')}...`);

                if (this.isValidTenant) {
                    this.storeLoginDetails();
                    await this.loginUser();
                }
                this.stopLoading();
            }
        },
        /**
         * Helper method to display the login error form
         * messages.
         * @param {String} message message string
         */
        displayLoginFormErrorMessage(message) {
            this.errorMessage = message;
        },
        /**
         * Validate one-time password.
         */
        async validateOTP() {
            try {
                // start loading
                this.beginLoading(`${this.$t('Authenticating')}...`);
                const payload = {
                    otp: this.oneTimePassword,
                    ephemeralToken: this.ephemeralToken
                };
                const response = await this.validateOtpAction(payload);

                // store the user tokens
                this.storeAuthTokens(
                    response.data.access,
                    response.data.refresh
                );
                // load the user to the state
                await this.loadUser();

                // navigate the user to facility page
                this.$router.push({ name: 'facility_list' });
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};

                // If unsuccessful, display an error message
                if (error && error.error && error.error.match(/expired/)) {
                    this.displayLoginFormErrorMessage(
                        this.$t(
                            'Sorry, but the one-time password or backup code you entered is incorrect or expired. Please try again.'
                        )
                    );
                } else {
                    this.displayLoginFormErrorMessage(
                        this.$t(
                            'Error while trying to validate one-time password.'
                        )
                    );
                }
            } finally {
                // stop loading
                this.stopLoading();
                this.submitCounter += 1;
            }
        },
        /**
         * Handle OTP input.
         * @param {Boolean} isValid - True if OTP is valid.
         */
        handleOTP(isValid) {
            this.isOtpValid = isValid;
            if (isValid) {
                this.validateOTP();
            }
        },
        /**
         * Extracts the UID and token from a given URL.
         * @param {string} url - The URL containing the UID and token.
         * @returns {Object} An object containing the UID and token.
         * @throws Will log an error message if the URL is invalid or extraction fails.
         */
        extractUidAndToken(url) {
            if (!url || typeof url !== 'string') {
                return { uid: null, token: null };
            }
            try {
                const urlObj = new URL(url);
                // Split the path into segments
                const parts = urlObj.pathname.split('/');
                // Second last segment is the UID
                const uid = parts[parts.length - 2];
                // Last segment is the token
                const token = parts[parts.length - 1];

                return { uid, token };
            } catch (error) {
                console.error(
                    'Error extracting the UID and Token from reset link',
                    error
                );
            }
        },
        /**
         * Send OTP to email.
         */
        async sendOTPToEmail() {
            // check if last code was sent 60 seconds ago
            if (this.emailOTPCoolDownSeconds > 0) {
                return;
            }

            this.startResendOTPCoolDown();

            // Re-authenticate the user to resend the OTP
            const response = await this.authenticateUser();

            // notify success if email is sent
            if (response.status === 200) {
                this.$notifySuccess(
                    this.$t('One-time password sent to your email.')
                );
            } else {
                this.$notifyError(
                    this.$t(
                        'Failed to send one-time password to your email. Please try again.'
                    )
                );
            }
        },
        /**
         * Start resend code cooldown.
         */
        startResendOTPCoolDown() {
            if (this.emailOTPResendInterval) {
                clearInterval(this.emailOTPResendInterval);
                this.emailOTPResendInterval = null;
            }

            this.emailOTPCoolDownSeconds = this.EMAIL_OTP_MAX_COOLDOWN_S;
            this.emailOTPResendInterval = setInterval(() => {
                if (this.emailOTPCoolDownSeconds === 0) {
                    clearInterval(this.emailOTPResendInterval);
                    this.emailOTPResendInterval = null;
                    return;
                }
                this.emailOTPCoolDownSeconds -= 1;
            }, 1000);
        },
        /**
         * Load SSO providers.
         */
        async loadSSOProviders() {
            this.ssoProviders = [];
            try {
                const response = await this.$axios.get(
                    '/auth/oauth/providers',
                    {
                        disableRequestInterceptor: true,
                        // Disable response interceptor to prevent the default error notifications
                        // from showing up if the request fails
                        disableResponseInterceptor: true
                    }
                );

                if ('providers' in response.data) {
                    for (const provider of response.data.providers) {
                        this.ssoProviders.push({
                            type: provider.type,
                            auth_url: provider.auth_url
                        });
                    }
                }
            } catch (error) {
                console.error('Error loading SSO providers', error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import '@css/auth_forms.scss';

.mfa-panel-header {
    padding-left: 10px;
    padding-top: 10px;
    padding-bottom: 15px;
}

.or-separator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    position: relative;
    margin: 16px 0;
}

.separator {
    flex: 1;
    height: 1px;
    background-color: #ccc;
}
</style>
